import CanvasChart from './-canvas-chart.ts';
import ChartTooltip from './-chart-tooltip.gts';
import { cached, tracked } from '@glimmer/tracking';
import { concat } from '@ember/helper';
import style from 'ember-style-modifier/modifiers/style';
import { setupCanvas, drawGridLines, drawYAxisLabels } from '../../utils/charts/canvas-utils.ts';
import { linearScale, bandScale, niceAxisTicks } from '../../utils/charts/chart-scales.ts';
import { computeLayout, formatAxisValue, topTooltipRows } from '../../utils/charts/chart-layout.ts';
import { DEFAULT_CHART_COLORS, getThemeColors } from '../../utils/charts/color-palette.ts';

// ComboChart – stacked/clustered bars + lines (optional secondary axis), with
// axis titles, configurable x-label angle, legend placement, optional data
// labels, and value formatters. One component for bar / stacked / line / combo.

export interface ComboChartSeries {
  /**
   * Per-label overrides of the series color, index-aligned with the chart labels;
   * undefined entries fall back to the series color. Bars only.
   */
  pointColors?: (string | undefined)[];
  label: string;
  data: number[];
  color?: string;
}

type LegendPosition = 'top' | 'right' | 'bottom' | 'left';

interface ComboChartSignature {
  Args: {
    labels: string[];
    barSeries: ComboChartSeries[];
    /**
     * Optional so bar-only consumers can omit it. A zero-argument `{{array}}` resolves to the
     * helper rather than an empty array and is non-nullish, so passing one would slip past the
     * `?? []` default and throw on `flatMap`.
     */
    lineSeries?: ComboChartSeries[];
    stacked?: boolean;
    secondaryAxis?: boolean;
    height?: number;
    formatValue?: (v: number) => string;
    formatValueRight?: (v: number) => string;
    // formatting
    xLabel?: string;
    yLabel?: string;
    yLabelRight?: string;
    labelAngle?: 0 | 45 | 90;
    showLegend?: boolean;
    legendPosition?: LegendPosition;
    showDataLabels?: boolean;
    /**
     * Accessible name for the chart. Also the caption of the data table that carries the
     * values for assistive tech, so name what the chart shows, not that it is a chart.
     */
    ariaLabel?: string;
  };
  Element: HTMLDivElement;
}

const DEFAULT_HEIGHT = 300;

interface ColumnHit {
  x: number;
  w: number;
  index: number;
}

export default class ComboChart extends CanvasChart<ComboChartSignature> {
  // Anchor the tooltip to the left of the cursor once the hovered column is past
  // the chart midpoint, so it never runs off the right edge.
  @tracked tooltipFlip = false;

  private columnHits: ColumnHit[] = [];

  get height() {
    return this.args.height ?? DEFAULT_HEIGHT;
  }

  get labels() {
    return this.args.labels ?? [];
  }
  get barSeries() {
    return this.args.barSeries ?? [];
  }
  get lineSeries() {
    return this.args.lineSeries ?? [];
  }
  get stacked() {
    return this.args.stacked !== false;
  }
  get hasRightAxis() {
    return this.args.secondaryAxis !== false && this.lineSeries.length > 0;
  }
  get labelAngle() {
    return this.args.labelAngle ?? 45;
  }
  get showLegend() {
    return this.args.showLegend !== false;
  }
  get legendPosition(): LegendPosition {
    return this.args.legendPosition ?? 'bottom';
  }
  get showDataLabels() {
    return this.args.showDataLabels === true;
  }

  /** Flex direction of the root so the legend sits on the chosen side. */
  get rootClass() {
    switch (this.legendPosition) {
      case 'top':
        return 'flex flex-col-reverse';
      case 'right':
        return 'flex flex-row';
      case 'left':
        return 'flex flex-row-reverse';
      case 'bottom':
      default:
        return 'flex flex-col';
    }
  }

  get legendIsSide() {
    return this.legendPosition === 'left' || this.legendPosition === 'right';
  }

  get legendClass() {
    return this.legendIsSide
      ? 'flex flex-col gap-1 justify-center text-xs tio-fg-muted px-2 shrink-0'
      : 'flex flex-wrap gap-x-4 gap-y-1 mt-2 justify-center text-xs tio-fg-muted';
  }

  barColor = (index: number) =>
    this.barSeries[index]?.color ?? DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length]!;

  lineColor = (index: number) => {
    const offset = this.barSeries.length + index;
    return (
      this.lineSeries[index]?.color ?? DEFAULT_CHART_COLORS[offset % DEFAULT_CHART_COLORS.length]!
    );
  };

  get legendItems() {
    const bars = this.barSeries.map((s, i) => ({
      label: s.label,
      isLine: false,
      color: this.barColor(i),
    }));
    const lines = this.lineSeries.map((s, i) => ({
      label: s.label,
      isLine: true,
      color: this.lineColor(i),
    }));
    return [...bars, ...lines];
  }

  get tooltipLabel() {
    if (this.hoveredIndex === null) return '';
    return this.labels[this.hoveredIndex] ?? '';
  }

  /**
   * The canvas is opaque to assistive tech and the tooltip is pointer-only, so the same
   * numbers are also rendered as a visually hidden table. Rows are series, columns are the
   * x-axis labels.
   */
  get dataTableRows() {
    const lineFormat = this.hasRightAxis ? this.fmtRight : this.fmtLeft;

    return [
      ...this.barSeries.map((s) => ({
        label: s.label,
        values: this.labels.map((_, i) => this.fmtLeft(s.data[i] ?? 0)),
      })),
      ...this.lineSeries.map((s) => ({
        label: s.label,
        values: this.labels.map((_, i) => lineFormat(s.data[i] ?? 0)),
      })),
    ];
  }

  get accessibleLabel() {
    return this.args.ariaLabel ?? this.args.yLabel ?? 'Chart';
  }

  fmtLeft = (v: number) => (this.args.formatValue ?? formatAxisValue)(v);
  fmtRight = (v: number) => (this.args.formatValueRight ?? this.fmtLeft)(v);

  @cached
  private get cappedTooltip() {
    if (this.hoveredIndex === null) return { rows: [], moreCount: 0 };
    const idx = this.hoveredIndex;
    const bars = this.barSeries.map((s, i) => {
      const raw = s.data[idx] ?? 0;
      return { raw, row: { label: s.label, color: this.barColor(i), value: this.fmtLeft(raw) } };
    });
    const lines = this.lineSeries.map((s, i) => {
      const raw = s.data[idx] ?? 0;
      return {
        raw,
        row: {
          label: s.label,
          color: this.lineColor(i),
          value: (this.hasRightAxis ? this.fmtRight : this.fmtLeft)(raw),
        },
      };
    });
    return topTooltipRows([...bars, ...lines]);
  }

  get tooltipRows() {
    return this.cappedTooltip.rows;
  }

  get tooltipMoreCount() {
    return this.cappedTooltip.moreCount;
  }

  get tooltipTransform() {
    // Sit above the cursor (translateY -100%); extend left when flipped, else right.
    const tx = this.tooltipFlip ? 'calc(-100% - 6px)' : '6px';
    return `translate(${tx}, -100%)`;
  }

  protected measuresContainerHeight = false;
  protected measuresOnNextTick = true;

  trackRedrawInputs() {
    void this.labels;
    void this.barSeries;
    void this.lineSeries;
    void this.args.legendPosition;
  }

  draw(canvas: HTMLCanvasElement) {
    const width = this.containerWidth;
    const height = this.height;
    const labels = this.labels;
    const barSeries = this.barSeries;
    const lineSeries = this.lineSeries;
    if (!labels.length) return;

    const angle = this.labelAngle;
    const hasRight = this.hasRightAxis;
    const theme = getThemeColors();
    const ctx = setupCanvas(canvas, width, height);
    ctx.clearRect(0, 0, width, height);

    // Value ranges (layout-independent) — computed first so the left margin can
    // be sized to the widest y-axis tick label.
    const barTotals = labels.map((_, ci) =>
      this.stacked
        ? barSeries.reduce((sum, s) => sum + (s.data[ci] ?? 0), 0)
        : Math.max(0, ...barSeries.map((s) => s.data[ci] ?? 0))
    );
    const barMax = Math.max(0, ...barTotals);
    const lineVals = lineSeries.flatMap((s) => s.data.filter((v) => isFinite(v)));
    const lineMax = lineVals.length ? Math.max(0, ...lineVals) : 0;
    const leftMax = hasRight ? barMax : Math.max(barMax, lineMax);

    // Bottom margin scales with the label angle + presence of an axis title.
    const labelBand = angle === 0 ? 34 : angle === 90 ? 96 : 78;
    const bottomMargin = labelBand + (this.args.xLabel ? 18 : 0);
    const topMargin = 14;
    // Tick count scales with the available height (which does not depend on the
    // left margin), so size it up front and measure the y-axis labels with the
    // SAME count we'll render: fewer ticks can yield a larger "nice" max (e.g.
    // 2,000,000 vs 1,600,000) and thus a wider label that would otherwise clip.
    const tickTarget = Math.max(
      2,
      Math.min(6, Math.floor((height - topMargin - bottomMargin) / 40))
    );
    // Left margin fits the widest formatted y-tick so labels never overlap the
    // axis title (fixed labels like "$5,000,000.00" overflow a static margin).
    ctx.font = '500 12px system-ui, sans-serif';
    const widestLeft = Math.max(
      0,
      ...niceAxisTicks(0, leftMax, tickTarget).map((t) => ctx.measureText(this.fmtLeft(t)).width)
    );
    const leftMargin = Math.max(52, Math.ceil(widestLeft) + 14) + (this.args.yLabel ? 16 : 0);
    const rightMargin = (hasRight ? 56 : 16) + (this.args.yLabelRight ? 16 : 0);
    const layout = computeLayout(width, height, {
      top: topMargin,
      bottom: bottomMargin,
      left: leftMargin,
      right: rightMargin,
    });

    const leftTicks = niceAxisTicks(0, leftMax, tickTarget);
    const yLeft = linearScale(
      [0, leftTicks[leftTicks.length - 1]!],
      [layout.top + layout.innerHeight, layout.top]
    );
    const rightTicks = hasRight ? niceAxisTicks(0, lineMax, tickTarget) : null;
    const yRight =
      rightTicks && rightTicks.length
        ? linearScale(
            [0, rightTicks[rightTicks.length - 1]!],
            [layout.top + layout.innerHeight, layout.top]
          )
        : null;

    drawGridLines(ctx, leftTicks, yLeft, layout.left, layout.left + layout.innerWidth, theme.grid);
    drawYAxisLabels(ctx, leftTicks, yLeft, layout.left - 8, theme.textMuted, this.fmtLeft);

    if (rightTicks && yRight) {
      ctx.save();
      ctx.textAlign = 'left';
      ctx.font = '10px system-ui, sans-serif';
      ctx.fillStyle = theme.textMuted;
      for (const tick of rightTicks) {
        ctx.fillText(this.fmtRight(tick), layout.left + layout.innerWidth + 6, yRight(tick) + 3);
      }
      ctx.restore();
    }

    const xScale = bandScale(labels, [layout.left, layout.left + layout.innerWidth], 0.2);
    const bw = xScale.bandwidth();
    const positions = labels.map((c) => xScale(c) + bw / 2);
    const baseY = yLeft(0);

    this.drawXLabels(
      ctx,
      labels,
      positions,
      layout.top + layout.innerHeight + 8,
      theme.textMuted,
      angle,
      layout.innerWidth
    );

    // Data labels are collected as candidates (each sits ABOVE its anchor y)
    // and placed in one collision-aware pass after all geometry is drawn, so
    // bar and line labels never overlap each other.
    const labelSpecs: { x: number; y: number; text: string }[] = [];

    this.columnHits = [];
    labels.forEach((label, ci) => {
      const x = xScale(label);
      if (this.stacked) {
        let cum = 0;
        barSeries.forEach((s, si) => {
          const v = s.data[ci] ?? 0;
          if (v <= 0) return;
          const yTop = yLeft(cum + v);
          const h = yLeft(cum) - yTop;
          ctx.fillStyle = s.pointColors?.[ci] ?? this.barColor(si);
          ctx.fillRect(x, yTop, bw, h);
          cum += v;
        });
        // One label per stack: the total, above the stack top.
        const total = barTotals[ci] ?? 0;
        if (this.showDataLabels && total > 0) {
          labelSpecs.push({ x: x + bw / 2, y: yLeft(total), text: this.fmtLeft(total) });
        }
      } else {
        const n = barSeries.length || 1;
        const subW = bw / n;
        barSeries.forEach((s, si) => {
          const v = s.data[ci] ?? 0;
          if (v <= 0) return;
          const yTop = yLeft(v);
          ctx.fillStyle = s.pointColors?.[ci] ?? this.barColor(si);
          ctx.fillRect(x + si * subW, yTop, subW * 0.88, baseY - yTop);
          if (this.showDataLabels) {
            labelSpecs.push({
              x: x + si * subW + (subW * 0.88) / 2,
              y: yTop,
              text: this.fmtLeft(v),
            });
          }
        });
      }
      this.columnHits.push({ x, w: bw, index: ci });
    });

    lineSeries.forEach((s, li) => {
      const yScale = hasRight && yRight ? yRight : yLeft;
      const fmt = hasRight ? this.fmtRight : this.fmtLeft;
      ctx.save();
      ctx.strokeStyle = this.lineColor(li);
      ctx.lineWidth = 2;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.beginPath();
      labels.forEach((_, ci) => {
        const x = positions[ci]!;
        const y = yScale(isFinite(s.data[ci]!) ? s.data[ci]! : 0);
        if (ci === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
      labels.forEach((_, ci) => {
        const x = positions[ci]!;
        const y = yScale(isFinite(s.data[ci]!) ? s.data[ci]! : 0);
        ctx.fillStyle = this.lineColor(li);
        ctx.beginPath();
        ctx.arc(x, y, 2.5, 0, Math.PI * 2);
        ctx.fill();
        if (this.showDataLabels) {
          labelSpecs.push({ x, y, text: fmt(s.data[ci] ?? 0) });
        }
      });
      ctx.restore();
    });

    if (this.showDataLabels) this.placeDataLabels(ctx, labelSpecs, layout.top, theme.text);

    this.drawAxisTitles(ctx, layout, theme.text);
  }

  /**
   * Draw value labels above their anchors without overlap: place left-to-right,
   * nudge a colliding label upward (stagger) until it clears, and drop it if
   * there's no room above the plot. Keeps dense bar+line combos legible.
   */
  private placeDataLabels(
    ctx: CanvasRenderingContext2D,
    specs: { x: number; y: number; text: string }[],
    topBound: number,
    color: string
  ) {
    const LH = 11;
    const GAP = 2;
    ctx.save();
    ctx.fillStyle = color;
    ctx.font = '600 10px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    const placed: { x0: number; x1: number; y0: number; y1: number }[] = [];
    const overlaps = (
      a: { x0: number; x1: number; y0: number; y1: number },
      b: { x0: number; x1: number; y0: number; y1: number }
    ) => a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0;

    for (const spec of [...specs].sort((a, b) => a.x - b.x)) {
      const halfW = ctx.measureText(spec.text).width / 2 + 2;
      let baseline = spec.y - GAP;
      let box = { x0: spec.x - halfW, x1: spec.x + halfW, y0: baseline - LH, y1: baseline };
      let ok = true;
      while (placed.some((p) => overlaps(p, box))) {
        baseline -= LH + GAP; // stagger upward
        box = { x0: spec.x - halfW, x1: spec.x + halfW, y0: baseline - LH, y1: baseline };
        if (box.y0 < topBound) {
          ok = false; // no room above the plot — drop it
          break;
        }
      }
      if (!ok) continue;
      placed.push(box);
      ctx.fillText(spec.text, spec.x, baseline);
    }
    ctx.restore();
    ctx.restore();
  }

  private drawXLabels(
    ctx: CanvasRenderingContext2D,
    labels: string[],
    positions: number[],
    y: number,
    color: string,
    angle: number,
    innerWidth: number
  ) {
    ctx.fillStyle = color;
    ctx.font = '500 11px system-ui, sans-serif';
    const maxChars = angle === 0 ? 12 : 18;
    const truncate = (raw: string) =>
      raw.length > maxChars ? `${raw.slice(0, maxChars - 1)}…` : raw;

    if (angle === 0) {
      // Horizontal labels: greedily skip any whose measured box would overlap
      // the last one drawn, so the interval thins out exactly as much as needed.
      const GAP = 8;
      let lastRight = -Infinity;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      for (let i = 0; i < labels.length; i++) {
        const text = truncate(labels[i] ?? '');
        const x = Math.round(positions[i]!);
        const halfW = ctx.measureText(text).width / 2;
        if (x - halfW < lastRight + GAP) continue;
        ctx.fillText(text, x, Math.round(y));
        lastRight = x + halfW;
      }
      return;
    }

    // Rotated labels occupy a small horizontal footprint — thin by a step from
    // the available width.
    const maxLabels = Math.max(2, Math.floor(innerWidth / 32));
    const step = labels.length > maxLabels ? Math.ceil(labels.length / maxLabels) : 1;
    for (let i = 0; i < labels.length; i++) {
      if (step > 1 && i % step !== 0 && i !== labels.length - 1) continue;
      const x = Math.round(positions[i]!);
      ctx.save();
      ctx.translate(x, Math.round(y));
      ctx.rotate((-angle * Math.PI) / 180);
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(truncate(labels[i] ?? ''), 0, 0);
      ctx.restore();
    }
  }

  private drawAxisTitles(
    ctx: CanvasRenderingContext2D,
    layout: { top: number; left: number; innerWidth: number; innerHeight: number; height: number },
    color: string
  ) {
    ctx.save();
    ctx.fillStyle = color;
    ctx.font = '600 11px system-ui, sans-serif';
    if (this.args.xLabel) {
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText(this.args.xLabel, layout.left + layout.innerWidth / 2, layout.height - 2);
    }
    if (this.args.yLabel) {
      ctx.save();
      ctx.translate(12, layout.top + layout.innerHeight / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(this.args.yLabel, 0, 0);
      ctx.restore();
    }
    if (this.args.yLabelRight && this.hasRightAxis) {
      ctx.save();
      ctx.translate(layout.left + layout.innerWidth + 48, layout.top + layout.innerHeight / 2);
      ctx.rotate(Math.PI / 2);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(this.args.yLabelRight, 0, 0);
      ctx.restore();
    }
    ctx.restore();
  }

  handleMouseMove(e: MouseEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const hit = this.columnHits.find((c) => x >= c.x && x <= c.x + c.w);
    if (hit) {
      if (this.hoveredIndex !== hit.index) {
        this.hoveredIndex = hit.index;
        this.draw(canvas);
      }
      this.tooltipX = rect.left + hit.x + hit.w / 2;
      this.tooltipY = e.clientY - 8;
      this.tooltipFlip = hit.x + hit.w / 2 > rect.width / 2;
    } else if (this.hoveredIndex !== null) {
      this.hoveredIndex = null;
      this.draw(canvas);
    }
  }

  <template>
    <div
      data-test-chart
      data-test-chart-type="combo"
      class="{{this.rootClass}} w-full h-full"
      ...attributes
    >
      <div class="relative min-w-0 flex-1" {{this.installCanvas}}>
        <canvas
          aria-hidden="true"
          {{style width=(concat this.containerWidth "px") height=(concat this.height "px")}}
        ></canvas>

        {{#if this.labels.length}}
          <table class="sr-only" data-test-chart-data-table>
            <caption>{{this.accessibleLabel}}</caption>
            <thead>
              <tr>
                <td>{{@xLabel}}</td>
                {{#each this.labels as |label|}}
                  <th scope="col">{{label}}</th>
                {{/each}}
              </tr>
            </thead>
            <tbody>
              {{#each this.dataTableRows as |row|}}
                <tr>
                  <th scope="row">{{row.label}}</th>
                  {{#each row.values as |value|}}
                    <td>{{value}}</td>
                  {{/each}}
                </tr>
              {{/each}}
            </tbody>
          </table>
        {{/if}}

        {{#if this.tooltipRows.length}}
          <ChartTooltip
            @x={{this.tooltipX}}
            @y={{this.tooltipY}}
            @transform={{this.tooltipTransform}}
            data-test-chart-tooltip
          >
            <div class="font-medium mb-1 tio-text-muted">{{this.tooltipLabel}}</div>
            {{#each this.tooltipRows as |row|}}
              <div class="tio-row-between gap-3">
                <span class="tio-row-extra-tight">
                  <span class="tio-chart-dot-swatch" {{style color=row.color}}></span>
                  {{row.label}}
                </span>
                <span class="font-medium">{{row.value}}</span>
              </div>
            {{/each}}
            {{#if this.tooltipMoreCount}}
              <div class="mt-1 text-xs tio-text-muted">+ {{this.tooltipMoreCount}} more</div>
            {{/if}}
          </ChartTooltip>
        {{/if}}
      </div>

      {{#if this.showLegend}}
        <div class={{this.legendClass}}>
          {{#each this.legendItems as |item|}}
            <span class="tio-row-snug">
              {{#if item.isLine}}
                <span
                  class="tio-chart-line-swatch tio-chart-line-swatch--sm"
                  {{style color=item.color}}
                ></span>
              {{else}}
                <span class="tio-chart-square-swatch" {{style color=item.color}}></span>
              {{/if}}
              {{item.label}}
            </span>
          {{/each}}
        </div>
      {{/if}}
    </div>
  </template>
}

export type { ComboChartSignature };
